/**
 * The two rules that take rows away.
 *
 * They are visually separated from the filters above them because they do the
 * opposite thing, and the previous version made them look identical: a row of
 * switches under a row of switches, where the ones above narrow the selection
 * and the ones below delete from it. Same control, opposite meaning, no way to
 * tell them apart - which is how somebody turns on "exclude free mailboxes" on a
 * register audience and loses two thirds of it without connecting the two facts.
 *
 * They ride inside the stored audience filter rather than on the campaign,
 * because they change WHO is selected. The preview counts what each one removed
 * rather than making the row vanish silently - see the screening module.
 */
import { Scissors } from "lucide-react";
import { Switch, cn } from "@wa/ui";
import { useT } from "../../../../../i18n/I18nProvider";

export interface Exclusions {
  excludeFreemail: boolean;
  excludeRoleAccounts: boolean;
}

function Rule({
  id,
  checked,
  onChange,
  disabled,
}: {
  id: "excludeFreemail" | "excludeRoleAccounts";
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  const t = useT();
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 transition-colors",
        checked ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-background/40 hover:bg-accent/30",
      )}
    >
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} className="mt-0.5" />
      <span className="min-w-0">
        <span className="block text-sm">{t(`marketing.audience.${id}`)}</span>
        <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
          {t(`marketing.audience.${id}Hint`)}
        </span>
      </span>
    </label>
  );
}

export function ExclusionsCard({
  value,
  onChange,
  disabled,
}: {
  value: Exclusions;
  onChange: (next: Exclusions) => void;
  disabled?: boolean;
}) {
  const t = useT();
  return (
    <section className="rounded-2xl border border-border bg-card/70 p-5 shadow-soft backdrop-blur">
      <header className="mb-3 flex items-start gap-2.5">
        <Scissors className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div>
          <h2 className="text-sm font-semibold tracking-tight">{t("marketing.audience.exclusions")}</h2>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {t("marketing.audience.exclusionsHint")}
          </p>
        </div>
      </header>
      <div className="grid gap-2 sm:grid-cols-2">
        <Rule
          id="excludeFreemail"
          checked={value.excludeFreemail}
          onChange={(v) => onChange({ ...value, excludeFreemail: v })}
          disabled={disabled}
        />
        <Rule
          id="excludeRoleAccounts"
          checked={value.excludeRoleAccounts}
          onChange={(v) => onChange({ ...value, excludeRoleAccounts: v })}
          disabled={disabled}
        />
      </div>
    </section>
  );
}
